import { HttpAgent, RunAgentInput } from '@ag-ui/client';
import { RunStartedEvent } from '@ag-ui/core';
import { isEmpty, isNil, isString } from 'lodash';
import {
  AskPromptAction,
  AskPromptBaseline,
  AskScope,
  buildAgUiRequestBody,
  postAgUiPrompt,
} from '../api';
import { ASK_GATEWAY_BASE_URL } from '../api/config';
import { AskPromptContext } from '../session/askSession';
import { lastUserAttachments, lastUserPrompt } from './lastUserPrompt';

/**
 * What `threadId` holds until the gateway has issued one — a conversation nobody has asked a
 * question in yet, and a first turn that therefore names no thread.
 *
 * A value rather than the absence of one because AG-UI's `AbstractAgent` types `threadId` as a
 * required `string` and mints a uuid of its own when handed nothing, which is exactly what this
 * has to stop. Empty is the value it can be, since `buildAgUiRequestBody` leaves an empty thread
 * id off the body.
 */
const NO_THREAD_ID = '';

interface CoastAskAgentConfig {
  /** Resolves the current business id at run time (may change as the user switches orgs). */
  getBusinessId: () => number | undefined;
  /** Resolves the active scope at run time (set when the panel is opened). */
  getScope: () => AskScope;
  /**
   * Resolves what the conversation is creating against at run time. Unlike the attachments,
   * which are read back off the transcript, this is nowhere in the messages — it is resolved
   * from the open context by the panel, and can change between turns. Optional because the
   * summary widgets create nothing, so they have no context to create against.
   */
  getPromptContext?: () => AskPromptContext;
  /**
   * Resolves what the turn is proposing over, for a surface that is refining something rather than
   * starting it. A getter for the same reason as the ones above: it is the state of a form the user
   * is still editing, and it is read as the body is built rather than when the agent is made.
   *
   * Optional because only Capture proposes at all — the panel and the summary widgets have nothing
   * to be a baseline of.
   */
  getPromptBaseline?: () => AskPromptBaseline | undefined;
  /**
   * Turns the newest user turn into the prompt that goes on the wire. Defaults to sending it
   * unchanged, which is what the panel wants; the summary widgets want a different answer from the
   * same gateway, so they pass their own (see `withSummaryDirectives`).
   */
  decoratePrompt?: (prompt: string) => string;
  /**
   * What the gateway should do with every turn this agent sends, when it is something other than
   * answer it. A fixed value rather than a getter, unlike everything above it: the action belongs
   * to the surface the agent was built for and cannot change between turns — Capture always
   * proposes, and the panel never does.
   */
  action?: AskPromptAction;
  /**
   * Whether the conversations this agent has are kept out of Ask history. A fixed value for the
   * same reason as `action`: it belongs to the surface the agent was built for.
   *
   * Defaults to unlisted, which makes listing the thing a surface opts into. The Ask panel is the
   * one surface whose turns are conversations the user comes back to, so it is the one that passes
   * `false` — and a surface built next stays out of history until somebody decides it belongs.
   */
  unlisted?: boolean;
}

/**
 * Drives the Coast Ask gateway with `protocol: 'ag-ui'`, so the gateway streams
 * AG-UI events and `HttpAgent` handles SSE parsing, protocol verification and
 * event → `Message[]` assembly (text streaming, tool-call argument
 * accumulation, tool results, reasoning) for us.
 *
 * Three things differ from a stock `HttpAgent`:
 *
 * - The gateway takes `{ prompt, attachments, threadId, scope, baseline, protocol }` rather than a
 *   `RunAgentInput`, so `requestInit` rewrites the body: the newest user turn becomes `prompt`
 *   and its files become `attachments`, while the turns before it are the gateway's own to
 *   remember, held against the thread id.
 * - Requests need a short-lived prompt token, minted asynchronously and retried
 *   on 401/403, which is what the injected `fetch` does.
 * - The thread id belongs to the gateway rather than to AG-UI, which mints one per agent:
 *   a new conversation asks with none and takes the one the answer carries (see
 *   `adoptThreadId`).
 */
export class CoastAskAgent extends HttpAgent {
  private readonly getBusinessId: () => number | undefined;
  private readonly getScope: () => AskScope;
  private readonly getPromptContext: () => AskPromptContext;
  private readonly decoratePrompt: (prompt: string) => string;
  private readonly getPromptBaseline: () => AskPromptBaseline | undefined;
  private readonly action: AskPromptAction | undefined;
  private readonly unlisted: boolean;

  constructor({
    getBusinessId,
    getScope,
    getPromptContext,
    getPromptBaseline,
    decoratePrompt,
    action,
    unlisted,
  }: CoastAskAgentConfig) {
    // `threadId` is passed explicitly so `AbstractAgent` does not mint one: handed nothing, it
    // falls back to a uuid of its own, and that uuid would go on the wire as a thread the
    // gateway never issued.
    super({
      url: `${ASK_GATEWAY_BASE_URL}/prompt`,
      debug: import.meta.env.DEV,
      threadId: NO_THREAD_ID,
    });
    this.getBusinessId = getBusinessId;
    this.getScope = getScope;
    this.getPromptContext = getPromptContext ?? (() => ({}));
    this.getPromptBaseline = getPromptBaseline ?? (() => undefined);
    this.decoratePrompt = decoratePrompt ?? (prompt => prompt);
    this.action = action;
    this.unlisted = unlisted ?? true;

    // Assigned after `super()` because `this` is unavailable in the argument list.
    this.fetch = (url, requestInit) => {
      const businessId = this.getBusinessId();
      if (isNil(businessId)) {
        return Promise.reject(new Error('Cannot run Ask without an active business'));
      }
      return postAgUiPrompt({
        businessId,
        url,
        body: String(requestInit.body),
        signal: requestInit.signal ?? undefined,
      });
    };

    this.subscribe({
      onRunStartedEvent: ({ event }) => this.adoptThreadId(event),
    });
  }

  /**
   * Starts a fresh conversation: empty transcript, no thread id. The agent is created once
   * and outlives every conversation, so ending one has to be explicit — carrying a thread id
   * across a cleared conversation would have the gateway continue the conversation the user
   * just cleared.
   */
  resetConversation(): void {
    this.abortRun();
    this.setMessages([]);
    this.threadId = NO_THREAD_ID;
  }

  /**
   * Starts a turn in an existing conversation: empty transcript, thread id kept.
   *
   * The difference from `resetConversation` is the whole of what makes a Capture multi-turn. Both
   * clear the local transcript — the gateway holds the conversation against the thread id, and
   * `requestInit` sends only the newest turn — but this one keeps the id, so the gateway supplies
   * everything said before it instead of opening a thread that has forgotten the card it is
   * halfway through building.
   *
   * `threadId` is where the conversation lives, so passing one adopts it: a surface that keeps the
   * conversation somewhere the agent does not — a Capture, whose agent is rebuilt each time its
   * modal opens — hands it back here. Passing nothing keeps whatever the agent already had.
   */
  startTurn(threadId?: string): void {
    this.abortRun();
    this.setMessages([]);
    if (isString(threadId) && !isEmpty(threadId)) {
      this.threadId = threadId;
    }
  }

  /** The gateway's id for the conversation so far, or undefined before it has issued one. */
  getThreadId(): string | undefined {
    return isEmpty(this.threadId) ? undefined : this.threadId;
  }

  protected requestInit(input: RunAgentInput): RequestInit {
    const scope = this.getScope();

    return {
      ...super.requestInit(input),
      // AG-UI keeps the user's turn verbatim for rendering. The panel sends it unchanged; a
      // surface with something of its own to say decorates the copy that goes on the wire.
      body: JSON.stringify(
        buildAgUiRequestBody({
          prompt: this.decoratePrompt(lastUserPrompt(input.messages)),
          // Fixed for the agent's lifetime, so it is read straight rather than through a getter.
          action: this.action,
          scope,
          // The agent's own field rather than `input.threadId`, which is a copy of it taken
          // when the run was prepared: this is the gateway's id for the conversation, and the
          // agent is where it is kept between turns.
          threadId: this.threadId,
          // Attachments come off the turn being sent rather than from anything held alongside it,
          // so an aborted-and-retried run carries exactly the files its question shows.
          attachments: lastUserAttachments(input.messages),
          // Dropped by `buildAgUiRequestBody` when the scope cannot carry it, which the gateway
          // refuses outright.
          viewTemplateId: this.getPromptContext().viewTemplateId,
          // What the turn is proposing over, when it is proposing over anything. A body field rather
          // than part of the prompt, so the form's state reaches the agent as data rather than as
          // words the user did not say.
          baseline: this.getPromptBaseline(),
          // Fixed for the agent's lifetime, like `action`: whether these conversations belong in
          // history is a fact about the surface, not about the turn.
          unlisted: this.unlisted,
        }),
      ),
    };
  }

  /**
   * Takes the thread id the gateway issued for this conversation, so every turn after the
   * first one names it. A new conversation asks with none, which is what tells the gateway to
   * start one.
   *
   * **This is the only place that knows where in the response the thread id lives.** It rides
   * on `RUN_STARTED` — a uuid, echoed unchanged on `RUN_FINISHED` — so it is in hand before
   * the answer has finished streaming. Only the start is read: AG-UI's schema makes `threadId`
   * required on it, so there is no case where the finish would carry one the start did not.
   *
   * The guard is against an empty value rather than a missing one, for the same reason: a
   * gateway that stopped filling it in would send `''`, and taking that would silently turn
   * every following turn back into a first turn.
   *
   * Whatever the newest run says wins, so a gateway that re-keys a conversation keeps the
   * next turn attached to it.
   */
  private adoptThreadId(event: RunStartedEvent): void {
    const { threadId } = event;
    if (isString(threadId) && !isEmpty(threadId)) {
      this.threadId = threadId;
    }
  }
}
